/**
  How to start a new form...
  docs: https://tanstack.com/form/latest/docs/framework/react/quick-start

  const form = useAppForm({
     defaultValues: {
         name: '',
     },
     validators: {
         // for client-side validation (1st step of form submission)
         onSubmit: zodSchema,
         // for server-side validation (2nd step of form submission)
         onSubmitAsync: async ({ value }) => {
             try {
                 // you'll need to write this mutation yourself
                 await mutation.mutateAsync(value, {
                     onSuccess: (value) => {
                         toast.success('Account created successfully')
                         // Import the usePathHelper hook for consistent navigation
                         return navigate({ ...pathHelper.accounts.show(value.id) })
                     },
                 })
             } catch (error) {
                 toast.error('Account creation failed', { description: getErrorMessage(error) })
                 // This takes Laravel validation errors and turns them into form errors
                 if(error?.response?.data?.errors) {
                     return { fields: error.response.data.errors }
                 }
             }
         },
     },
     // for logic after successful form submission (3rd step of form submission)
     onSubmit: () => {
        // The logic in the mutation onSuccess function above could also go here.
        // Because onSubmitAsync runs first, the mutation's onSuccess function will run
        // before anything you put in here. Basically, I never use this callback.
     },
  })

     return (
     // This `form` variable also grants us access to all the formComponents
     // defined in `createFormHook` in this file
     <form.AppForm>  // establish a context for the whole form
        <form.Form>  // an HTML form component with the onSubmit={handleSubmit} already set
            <form.AppField name="name">  // establish a context for a single field
                // The `name` prop connects this field to your form's data.
                // `variants[${index}].media.id` is a valid `name` prop representing a path to the value in your data
                // Note: this `field` variable could more appropriately be called `fieldComponents`
                // as it gives you access to all of the fieldComponents defined in this file
                {(field) => <field.TextInput id="name" label="Name" />} // render a text input
            </form.AppField>
            <SaveBar mutation={mutation} /> // The mutation is just included for loading state purposes
            // You should just import the <SaveBar /> component instead of manuall creating a submit button,
            // but if you absolutely need one, here's how to do it...
            <form.SubmitButton mutation={mutation}>  // render a submit button
                Submit
            </form.SubmitButton>
        </form.Form>
     </form.AppForm>
     )
  })
 */
import AddressSearch from '@/components/form/AddressSearch'
import AutoComplete from '@/components/form/AutoComplete'
import BarcodeListInput from '@/components/form/BarcodeListInput'
import CheckboxGroupInput from '@/components/form/CheckboxGroupInput'
import CheckboxInput from '@/components/form/CheckboxInput'
import ColorInput from '@/components/form/ColorInput'
import CustomerSearch from '@/components/form/CustomerSearch'
import { DatePicker } from '@/components/form/DatePicker'
import { DecimalFieldInput } from '@/components/form/DecimalInput'
import EmailInput from '@/components/form/EmailInput'
import Form from '@/components/form/Form'
import FormError from '@/components/form/FormError'
import HourInput from '@/components/form/HourInput'
import MediaUploadInput from '@/components/media/MediaUploadInput'
import MoneyInput from '@/components/form/MoneyInput'
import OccasionPicker from '@/components/form/OccasionPicker'
import PercentInput from '@/components/form/PercentInput'
import PhoneInput from '@/components/form/PhoneInput'
import RadioButtonGroup from '@/components/form/RadioButtonGroup'
import RichTextInput from '@/components/form/RichTextInput'
import SelectInput from '@/components/form/SelectInput'
import SliderInput from '@/components/form/SliderInput'
import SocialUrlInput from '@/components/form/SocialUrlInput'
import SubmitButton from '@/components/form/SubmitButton'
import SwitchInput from '@/components/form/SwitchInput'
import TextareaInput from '@/components/form/TextareaInput'
import TextInput from '@/components/form/TextInput'
import UrlInput from '@/components/form/UrlInput'
import {
  createFormHook,
  standardSchemaValidators,
  type StandardSchemaV1,
} from '@tanstack/react-form'
import { fieldContext, formContext } from './form-contexts'
import { isRecord } from '@/lib/utils'

// Add your custom field/form components here!
export const { useAppForm, withForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    AddressSearch,
    AutoComplete,
    BarcodeListInput,
    CheckboxGroupInput,
    CheckboxInput,
    ColorInput,
    CustomerSearch,
    DatePicker,
    DecimalInput: DecimalFieldInput,
    EmailInput,
    HourInput,
    MediaUploadInput,
    MoneyInput,
    OccasionPicker,
    PercentInput,
    PhoneInput,
    RadioButtonGroup,
    RichTextInput,
    SelectInput,
    SliderInput,
    SocialUrlInput,
    SwitchInput,
    TextInput,
    TextareaInput,
    UrlInput,
  },
  formComponents: {
    Form,
    FormError,
    SubmitButton,
  },
})

// For a form-level `onSubmit` Zod schema whose input type is not the form values, such as one that
// coerces typed strings to numbers. TanStack Form's `onSubmit: schema` shorthand requires the schema
// input to match the values; this runs the same Standard Schema check the shorthand runs.
export function submitSchemaValidator(schema: StandardSchemaV1) {
  return ({ value }: { value: unknown }) =>
    standardSchemaValidators.validate({ value, validationSource: 'form' }, schema)
}

// Laravel validation errors (`response.data.errors`) as form field errors, for `onSubmitAsync`.
export function serverFieldErrors(error: unknown) {
  const response = isRecord(error) ? error.response : undefined
  const data = isRecord(response) ? response.data : undefined

  return isRecord(data) && data.errors ? { fields: data.errors } : undefined
}
